import { useMemo } from 'react';
import { addDays } from 'date-fns';

import { DataTable, useDataTable, useTableSearchCondition } from '@kc-console/shared-ui';

import useGetServiceStatus from '../model/useGetServiceStatus';
import useServiceStatusColumns, {
  getServiceStatusHeadCellLabel,
  SERVICE_STATUS_COLUMN_COUNT,
} from './useServiceStatusColumns';
import useServiceStatusFilters from './useServiceStatusFilters';

import type { SearchCondition } from '@kc-console/shared-ui';
import type { GetServiceStatusRequest } from '../model/useGetServiceStatus';

const getServiceStatusRequestParams = (
  searchCondition: SearchCondition,
): GetServiceStatusRequest => {
  const serviceNameRaw = searchCondition.filter.find(f => f.id === 'serviceName')?.values?.[0];
  const serviceName =
    typeof serviceNameRaw === 'string' && serviceNameRaw.trim() !== ''
      ? serviceNameRaw.trim()
      : undefined;

  const sortState = searchCondition.sort[0];
  const sort = sortState?.id === 'name' ? `${sortState.id},${sortState.direction}` : undefined;

  return {
    ...(serviceName ? { serviceName } : {}),
    ...(sort ? { sort } : {}),
  };
};

/**
 * 서비스 상태 목록 화면의 본문.
 * @description
 * - 요약 배너, RSS 피드 버튼, API 응답 전체 목록을 표시하는 서비스 상태 테이블을 구성한다.
 * - 리전 필터는 GNB에서 관리하며 URL query string(region)으로 전달된다.
 * @example
 * <ServicesContent />
 */
const ServicesContent = () => {
  const filters = useServiceStatusFilters();

  const {
    filterState,
    setFilterState,
    sortState,
    setSortState,
    tableSettings,
    setTableSettings,
    searchCondition,
  } = useTableSearchCondition({
    tableKey: 'services-status-table',
    filters,
    enableQuerySync: { filter: true, sort: true },
  });

  const requestParams = useMemo(
    () => getServiceStatusRequestParams(searchCondition),
    [searchCondition],
  );

  const { data, isLoading, isError, isFetching, refetch, error } =
    useGetServiceStatus(requestParams);

  const serviceList = useMemo(() => data?.serviceList ?? [], [data?.serviceList]);

  const statusHeaderLabels = useMemo(
    () =>
      Array.from({ length: SERVICE_STATUS_COLUMN_COUNT }, (_, index) => {
        const fallbackDate = addDays(new Date(), -index).toISOString();
        const date = serviceList[0]?.statusList?.[index]?.date ?? fallbackDate;

        return getServiceStatusHeadCellLabel(date);
      }),
    [serviceList],
  );

  const columns = useServiceStatusColumns({ statusHeaderLabels });

  const { tableInstance } = useDataTable({
    columns,
    data: serviceList,
    getRowId: 'serviceName',
    enableRowSelection: false,
    tableSettings,
    onTableSettingsChange: setTableSettings,
  });

  return (
    <DataTable
      table={tableInstance}
      isLoading={isLoading}
      isError={isError}
      error={error}
      isFetching={isFetching}
      onRefetch={refetch}
      filter={{
        filterDefs: filters,
        filters: filterState,
        onFiltersChange: setFilterState,
      }}
      sorting={{
        sorting: sortState,
        onSortingChange: setSortState,
      }}
      stickyHeader
      showPagination={false}
      showSetting={{ showPageSizeSelect: false }}
      showSideBorder={false}
      showSideMargin
    />
  );
};

export default ServicesContent;
